import { useState } from 'react';
import type { TaskSummary } from '../../data/client';
import { useAnnotationUsers, useDeleteTask, useUpdateTask } from '../../data/hooks';
import {
  BottomSheet,
  Button,
  Dialog,
  FormField,
  Input,
  SelectMenu,
  Textarea,
  useIsDesktop,
  useToast,
} from '../../ui';

const UNASSIGNED = 'unassigned';

/**
 * EDITING ONE TASK: title, detail, assignee, due date, delete
 * (notes-tasks-comments.md 12.2).
 *
 * A `BottomSheet` on a phone and a centred `Dialog` on a desktop, which is one component
 * either way because `BottomSheet` IS a `Dialog` preset. Both are CHROME, so both are
 * allowed the glass material; every input inside them is opaque iOS-form styling,
 * because inputs carry content.
 */
export interface TaskEditorProps {
  readonly task: TaskSummary;
  readonly objectId: string;
  readonly recordId: string;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

export function TaskEditor({ task, objectId, recordId, open, onOpenChange }: TaskEditorProps) {
  const isDesktop = useIsDesktop();
  const users = useAnnotationUsers();
  const updateTask = useUpdateTask(objectId, recordId);
  const deleteTask = useDeleteTask(objectId, recordId);
  const toast = useToast();

  const [title, setTitle] = useState(task.title);
  const [body, setBody] = useState(task.body ?? '');
  const [assignee, setAssignee] = useState(task.assigneeUserId ?? UNASSIGNED);
  const [dueDate, setDueDate] = useState(task.dueDate ?? '');

  const save = (): void => {
    const trimmed = title.trim();
    if (trimmed === '') return;
    updateTask.mutate(
      {
        taskId: task.id,
        patch: {
          title: trimmed,
          /** Null CLEARS, absent leaves alone - the same distinction the API draws. */
          body: body.trim() === '' ? null : body.trim(),
          assigneeUserId: assignee === UNASSIGNED ? null : assignee,
          dueDate: dueDate === '' ? null : dueDate,
        },
      },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  /**
   * DEACTIVATED USERS ARE LISTED BUT NOT SELECTABLE. Omitting them entirely would make
   * the current assignee of an inherited task vanish from its own picker; the server
   * refuses assigning new work to them, and the disabled row is that rule made visible.
   */
  const options = [
    { value: UNASSIGNED, label: 'Unassigned' },
    ...(users.data ?? []).map((user) => ({
      value: user.id,
      label: user.isActive ? user.name : `${user.name} (deactivated)`,
      disabled: !user.isActive && user.id !== task.assigneeUserId,
    })),
  ];

  const Surface = isDesktop ? Dialog : BottomSheet;

  return (
    <Surface
      open={open}
      onOpenChange={onOpenChange}
      title="Edit task"
      footer={
        <div className="flex w-full items-center justify-between gap-3">
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            icon="trash"
            onClick={() =>
              deleteTask.mutate(task.id, {
                onSuccess: () => {
                  toast({ title: 'Task deleted', tone: 'neutral' });
                  onOpenChange(false);
                },
              })
            }
          >
            Delete
          </Button>
          <Button variant="primary" onClick={save} loading={updateTask.isPending}>
            Save
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <FormField label="Title">
          {(props) => (
            <Input
              {...props}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              invalid={title.trim() === ''}
            />
          )}
        </FormField>
        <FormField label="Detail">
          {(props) => (
            <Textarea
              {...props}
              value={body}
              onChange={(event) => setBody(event.target.value)}
              rows={3}
            />
          )}
        </FormField>
        <FormField label="Assignee">
          {(props) => (
            <SelectMenu
              id={props.id}
              value={assignee}
              onValueChange={setAssignee}
              options={options}
              ariaLabel="Assignee"
            />
          )}
        </FormField>
        <FormField label="Due date">
          {/* A plain date input: there is no time to pick, because there is no reminder. */}
          {(props) => (
            <Input
              {...props}
              type="date"
              value={dueDate}
              onChange={(event) => setDueDate(event.target.value)}
            />
          )}
        </FormField>
      </div>
    </Surface>
  );
}
